/** @format */

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  ArrowLeftIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  ClipboardCheck,
  DownloadCloud,
  FileText,
  GripVertical,
  Loader,
  Loader2,
  Lock,
  Plus,
  Sparkles,
  Upload,
} from "lucide-react";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { sopService } from "../../api/sopService";
import { ReferencePanel } from "../../components/sop/ReferencePanel";
import SopAccessDenied from "../../components/sop/SopAccessDenied";
import { Button } from "../../components/ui/button";
import Header from "../../components/ui/Header";
import "../../components/ui/Header.css";
import InputField from "../../components/ui/InputField";
import Toast from "../../components/ui/Toast";
import "../../components/ui/Toast.css";
import { useImageHandling } from "../../hooks/useImageHandling";
import useMarkdownExport from "../../hooks/useMarkdownExport";
import { useOrgRole } from "../../hooks/useOrgRole";
import usePdfGenerate from "../../hooks/usePdfGenerate";
import { useSopMedia } from "../../hooks/useSopMedia";
import { useSuperAdmin } from "../../hooks/useSuperAdmin";
import { useToast } from "../../hooks/useToast";
import { track } from "../../lib/posthog";
import { useDocumentPreviewStore } from "../../store/useDocumentPreviewStore";
import { useFeedbackStore } from "../../store/useFeedbackStore";
import { useRegeneratedStepsStore } from "../../store/useReviewCommitStore";
import { useSopStore } from "../../store/useSopStore";
import { useTrackChangesState } from "../../store/useTrackChangesStore";
import {
  ProcessingStatusEnum,
  SOP,
  SOPVersion,
  StatementOfOperationsOutput,
  Step,
} from "../../types/sop";
import { extractImageReferences } from "../../utils/imageReferences";
import { ensurePartIds, ensureToolIds } from "../../utils/stepIdentity";
import { formatCycleTime } from "../../utils/time";
import { processAnnotatedImages } from "../../utils/uploadAnnotatedImage";
import { processCroppedImages } from "../../utils/uploadCroppedImage";
import Modal from "../../components/ui/Modal";
import StartReviewModal from "../review-commit/StartReviewModal";
import "./SOPEditor.css";
import { StepCandidateReview } from "./StepCandidateReview";
import StepEditor from "./StepEditor";
import {
  getStepNavStatus,
  getStepNavStatusAriaLabel,
  getStepNavStatusTitle,
  stepNavStatusToSuffix,
  type StepNavStatus,
} from "./stepNavStatus";
import { TITLE_PAGE_FEEDBACK_STEP_INDEX } from "./feedbackStepIndex";
import { FieldFeedbackButton } from "./FieldFeedbackButton";
import { ReleasedVersionBanner } from "./SOPTitle";

interface SortableStepItemProps {
  step: Step;
  stepIndex: number;
  isActive: boolean;
  isEditable: boolean;
  onStepClick: (index: number) => void;
  canEdit: boolean;
  isReorderDisabled: boolean;
  isStepDisabled: boolean;
  status: StepNavStatus;
}

function StepNavStatusIcon({ status }: { status: StepNavStatus }) {
  if (status !== "locked") return null;
  return (
    <Lock
      className="w-3.5 h-3.5 shrink-0 pointer-events-none text-slate-500"
      aria-hidden
    />
  );
}

const SortableStepItem: FC<SortableStepItemProps> = ({
  step,
  stepIndex,
  isActive,
  isEditable,
  onStepClick,
  canEdit,
  isReorderDisabled,
  isStepDisabled,
  status,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: stepIndex.toString(),
    transition: {
      duration: 200,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
    disabled: isReorderDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 1000 : "auto",
  };

  const handleClick = () => {
    if (isStepDisabled || isDragging) return;
    onStepClick(stepIndex);
  };

  const handleClasses = () => {
    const defaultClasses = `relative rounded-lg transition-all duration-300 ease-in-out p-[2px] group select-none`;
    const editableClasses = `cursor-grab active:cursor-grabbing`;
    const disabledClasses = `cursor-default`;
    const stepDisabledClasses = `opacity-50 cursor-not-allowed hover:from-transparent hover:via-transparent hover:to-transparent hover:shadow-none`;
    const draggingClasses = `shadow-2xl shadow-blue-500/20 scale-105`;
    const activeClasses = `bg-gradient-to-r from-[#3C6AC6] via-[#D216CC] to-[#FB6B96] shadow-lg shadow-blue-500/20`;
    const hoverClasses = `bg-transparent hover:bg-gradient-to-r hover:from-[#3C6AC6] hover:via-[#D216CC] hover:to-[#FB6B96] hover:shadow-lg hover:shadow-blue-500/20`;

    if (isEditable) {
      return `${defaultClasses} ${editableClasses}`;
    }

    if (isReorderDisabled) {
      return `${defaultClasses} ${disabledClasses}`;
    }

    if (isStepDisabled) {
      return `${defaultClasses} ${stepDisabledClasses}`;
    }

    if (isDragging) {
      return `${defaultClasses} ${draggingClasses}`;
    }

    if (isActive) {
      return `${defaultClasses} ${activeClasses}`;
    }

    return `${defaultClasses} ${hoverClasses}`;
  };
  const statusSuffix = stepNavStatusToSuffix(status);
  const statusTitle = getStepNavStatusTitle(status);
  const rowTitle = isStepDisabled
    ? "No AI version for this step; only steps with AI versions are available in review"
    : statusTitle
      ? `${step.title} — ${statusTitle}`
      : step.title;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(isEditable ? attributes : {})}
      {...(isEditable ? listeners : {})}
      {...(isReorderDisabled ? {} : { ...attributes, ...listeners })}
      onClick={handleClick}
      title={rowTitle}
      aria-label={getStepNavStatusAriaLabel(status, step.title, stepIndex + 1)}
      className={handleClasses()}
    >
      <div className="w-full h-full bg-gradient-to-br from-gray-900/95 to-black/95 rounded-lg p-3 flex items-center gap-3">
        {isEditable && !isReorderDisabled && canEdit && (
          <div className="flex-shrink-0">
            <GripVertical className="w-4 h-4 text-slate-400 group-hover:text-slate-300 transition-colors duration-200" />
          </div>
        )}
        <div
          className={`sop-editor-step-badge sop-editor-step-badge--${statusSuffix} ${isActive ? "sop-editor-step-badge--current" : ""}`}
        >
          {stepIndex + 1}
        </div>
        <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-sm font-medium text-white leading-relaxed truncate min-w-0">
              {step.title}
            </div>
            <StepNavStatusIcon status={status} />
          </div>
          {step.cycleTime != null && step.cycleTime > 0 && (
            <span className="text-slate-400 text-sm flex-shrink-0 tabular-nums">
              {formatCycleTime(step.cycleTime)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const SopEditor: FC = () => {
  const { sopId } = useParams<{ sopId: string }>();
  const {
    setSelectedSOP,
    latestVersion,
    selectedVersion,
    getSelectedSOP,
    getSopById,
    refreshSelectedSOP,
    addStep,
    deleteStep,
    reorderSteps,
    saveSOPChanges,
    hasUnsavedChanges,
    isUpdating,
    isLoading,
    setHasUnsavedChanges,
    updateSopTitleById,
    getVersionList,
    setSelectedVersion,
    fetchSOPs,
    error: sopError,
  } = useTrackChangesState();
  const { fetchSOPById, completeVersion, createNewDraft } = useSopStore();

  const selectedSOP = getSelectedSOP();
  const displayTitle = selectedSOP?.getDisplayTitle();
  const isEditable = selectedSOP?.isLatestVersionDraft() ?? true;
  const versionInContext = selectedVersion ?? latestVersion;
  const targetVersionId =
    versionInContext?.versionId ?? latestVersion?.versionId;
  const versionList = getVersionList(sopId ?? "");
  const isViewingDoneVersion =
    versionInContext != null && versionInContext.status !== "draft";
  const hasDraftVersion = versionList.some((v) => v.status === "draft");
  const draftVersionId = versionList.find(
    (v) => v.status === "draft",
  )?.versionId;

  const [completeVersionOpen, setCompleteVersionOpen] = useState(false);
  const [completeVersionDescription, setCompleteVersionDescription] =
    useState("");
  const [isCompletingVersion, setIsCompletingVersion] = useState(false);
  const [downloadReleaseChoiceOpen, setDownloadReleaseChoiceOpen] =
    useState(false);
  const [downloadFormatPickerOpen, setDownloadFormatPickerOpen] =
    useState(false);
  const [releaseThenDownloadMode, setReleaseThenDownloadMode] = useState(false);
  const [isVersionSwitchLoading, setIsVersionSwitchLoading] = useState(false);
  const [isCreatingNewDraft, setIsCreatingNewDraft] = useState(false);
  const [leaveStepChangesConfirmOpen, setLeaveStepChangesConfirmOpen] =
    useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{
    path: string;
    state?: object;
  } | null>(null);

  const { isSuperAdmin } = useSuperAdmin();
  const { canDownload } = useOrgRole();
  // Only super admins can edit any field in the SOP
  const canEdit = isSuperAdmin;
  // Rich-text body of steps is editable only for super_admin
  const canEditStepContent = isSuperAdmin;
  const [index, setIndex] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingCandidates, setIsSendingCandidates] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const {
    imageUrls,
    isLoadingImages,
    setIsLoadingImages,
    handleFileUpload,
    getImageUrls,
    isValidImageRefId,
    normalizeImageRefId,
    setImageUrls,
    getCurrentUrl,
    resetProcessedKeys,
  } = useImageHandling();

  const {
    referenceFiles,
    referenceFileKinds,
    defaultReferenceFileKind,
    selectedStepIndices,
    selectedFeedbackIds,
    setReferenceFiles,
    setDefaultReferenceFileKind,
    setReferenceFileKind,
    setSelectedStepIndices,
    setSelectedFeedbackIds,
    clearCandidates,
    clearDecisions,
    regenerate,
    isRegenerating,
    error: regenerateError,
    reviewMode,
    setReviewMode,
    candidates,
    setCandidates,
    decide,
    undecide,
    decisions,
    candidatesDirty,
    updateCandidate,
    saveCandidates,
    applyCandidateDecisions,
  } = useRegeneratedStepsStore();

  const [lockedStepIndices, setLockedStepIndices] = useState<number[]>([]);
  const [ownerCanReview, setOwnerCanReview] = useState(false);
  const [ownerVisibleAny, setOwnerVisibleAny] = useState(false);
  const hasLockedSteps = lockedStepIndices.length > 0;
  const isRegenerationRequested =
    selectedSOP?.processingStatus === ProcessingStatusEnum.REGENERATE;
  const isSuperAdminRegeneratingReview =
    isSuperAdmin &&
    selectedSOP?.processingStatus === ProcessingStatusEnum.REGENERATE &&
    reviewMode;
  const showApproveDenyActions = !isSuperAdmin && ownerCanReview;
  const lockedStepSet = useMemo(
    () => new Set(lockedStepIndices.filter((x) => Number.isFinite(x))),
    [lockedStepIndices],
  );
  const isSuperAdminWaitingForOwnerReview =
    isSuperAdmin && isRegenerationRequested && ownerVisibleAny;

  const {
    media,
    loading: mediaLoading,
    error: mediaError,
  } = useSopMedia(sopId || "");

  const loadedImageIdsRef = useRef<Set<string>>(new Set());
  const loadedCandidatesForRef = useRef<{
    sopId: string;
    versionId: string;
  } | null>(null);
  const loadedVersionFromUrlRef = useRef<{
    sopId: string;
    versionId: string;
  } | null>(null);
  const previousSopIdRef = useRef<string | null>(null);
  /** When set, format-picker download should trigger navigate to title page with this version after download. */
  const pendingPostReleaseNavigateRef = useRef<{
    completed: SOPVersion;
    sopId: string;
  } | null>(null);
  const hasWarnedEditWithCandidatesRef = useRef(false);
  /** Baseline steps (last saved or initially loaded). Compare current steps to this to set hasUnsavedChanges so "type then erase" clears the Save button. Updated on nav, after fetch, and after save. */
  const lastSavedStepsRef = useRef<Step[] | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stepParam = searchParams.get("step");
  const versionIdParam = searchParams.get("versionId");
  const feedbackIdParam = searchParams.get("feedbackId");

  useEffect(() => {
    if (!sopId) return;
    setSelectedSOP(sopId);
    const applyStepFromUrl = () => {
      const stepNum =
        stepParam != null && stepParam !== "" ? Number(stepParam) : 0;
      setIndex(Number.isFinite(stepNum) && stepNum >= 0 ? stepNum : 0);
    };
    const applyDefaultVersionToUrl = (id: string) => {
      if (searchParams.get("versionId") === id) return;
      const next = new URLSearchParams(searchParams);
      next.set("versionId", id);
      setSearchParams(next, { replace: true });
    };
    // Only reset unsaved flag when navigating to a different SOP; avoid clearing it on effect re-runs from other deps
    if (previousSopIdRef.current !== sopId) {
      previousSopIdRef.current = sopId;
      setHasUnsavedChanges(false);
      const initialSteps = getSopById(sopId)?.getLatestVersion()?.steps;
      lastSavedStepsRef.current = initialSteps
        ? initialSteps.map((s) => ({ ...s }))
        : null;
      clearCandidates();
      setReviewMode(false);
      setIndex(0);
    }
    // Refetch only when we don't have full SOP data (avoids redundant reloads and repeated requests)
    const existingSop = getSopById(sopId);
    const needsFullSop =
      !existingSop ||
      (existingSop.getLatestVersion() &&
        (existingSop.getLatestVersion()!.steps?.length ?? 0) === 0 &&
        existingSop.getOperationCount() > 0);
    const needVersionFromUrl =
      versionIdParam &&
      !(
        loadedVersionFromUrlRef.current?.sopId === sopId &&
        loadedVersionFromUrlRef.current?.versionId === versionIdParam
      ) &&
      (!existingSop || versionInContext?.versionId !== versionIdParam);

    if (needVersionFromUrl) {
      fetchSOPById(sopId, versionIdParam)
        .then(() => {
          loadedVersionFromUrlRef.current = { sopId, versionId: versionIdParam };
          refreshSelectedSOP();
          applyStepFromUrl();
        })
        .catch(() => {
          fetchSOPById(sopId).then(() => {
            loadedVersionFromUrlRef.current = { sopId, versionId: versionIdParam };
            refreshSelectedSOP();
            const latestId = getSopById(sopId)?.getLatestVersion()?.versionId;
            if (latestId) {
              applyDefaultVersionToUrl(latestId);
            }
            const steps = getSopById(sopId)?.getLatestVersion()?.steps;
            lastSavedStepsRef.current = steps
              ? steps.map((s) => ({ ...s }))
              : null;
            setHasUnsavedChanges(false);
            applyStepFromUrl();
          });
        });
    } else if (needsFullSop) {
      fetchSOPById(sopId).then(() => {
        refreshSelectedSOP();
        const steps = getSopById(sopId)?.getLatestVersion()?.steps;
        lastSavedStepsRef.current = steps ? steps.map((s) => ({ ...s })) : null;
        setHasUnsavedChanges(false);
        applyStepFromUrl();
        const latestId = getSopById(sopId)?.getLatestVersion()?.versionId;
        if (latestId && !versionIdParam) {
          applyDefaultVersionToUrl(latestId);
        }
      });
    } else {
      if (!versionIdParam) {
        const latestId = getSopById(sopId)?.getLatestVersion()?.versionId;
        if (latestId) {
          applyDefaultVersionToUrl(latestId);
        }
      }
      if (stepParam != null && stepParam !== "") {
        applyStepFromUrl();
      }
    }
  }, [
    sopId,
    fetchSOPById,
    setSelectedSOP,
    getSopById,
    clearCandidates,
    setReviewMode,
    setHasUnsavedChanges,
    refreshSelectedSOP,
    versionIdParam,
    stepParam,
    versionInContext?.versionId,
    searchParams,
    setSearchParams,
  ]);

  const refreshLocks = useCallback(async () => {
    if (!sopId || !targetVersionId) return;
    try {
      const res = await sopService.getRegenerationLocks(sopId, targetVersionId);
      setLockedStepIndices(res.lockedStepIndices ?? []);
      setOwnerCanReview(Boolean(res.ownerCanReview));
      setOwnerVisibleAny(Boolean(res.ownerVisibleAny));
    } catch {
      // best-effort
    }
  }, [sopId, targetVersionId]);

  // Ensure the /locks request actually fires once we have a versionId.
  useEffect(() => {
    if (!sopId || !targetVersionId) return;
    refreshLocks();
  }, [sopId, targetVersionId, refreshLocks]);

  // As soon as the backend flips the SOP into `regenerate`, fetch locks
  // immediately so we can lock only the requested indices.
  useEffect(() => {
    if (!isRegenerationRequested) return;
    if (ownerCanReview) return; // once visible to owner, review-mode flow takes over
    void refreshLocks();
  }, [isRegenerationRequested, ownerCanReview, refreshLocks]);

  // After "Submit for Review", the super-admin must not remain in review mode.
  // Exit review mode so we render locked original steps (no candidate editor).
  useEffect(() => {
    if (!isSuperAdminWaitingForOwnerReview) return;
    if (!reviewMode) return;
    setReviewMode(false);
  }, [isSuperAdminWaitingForOwnerReview, reviewMode, setReviewMode]);

  // When version loads async (e.g. after fetchSOPById), set baseline so revert-to-saved can clear the button
  useEffect(() => {
    if (!versionInContext?.steps?.length || lastSavedStepsRef.current != null)
      return;
    lastSavedStepsRef.current = versionInContext.steps.map((s) => ({ ...s }));
  }, [versionInContext?.steps]);

  const setFocusedSegment = useDocumentPreviewStore((s) => s.setFocusedSegment);
  const setPreviewFileId = useDocumentPreviewStore((s) => s.setSelectedFileId);
  const fetchFeedbackForVersion = useFeedbackStore(
    (s) => s.fetchFeedbackForVersion,
  );
  const deleteFeedback = useFeedbackStore((s) => s.deleteFeedback);
  const setSelectedFeedbackIdForStep = useFeedbackStore(
    (s) => s.setSelectedFeedbackIdForStep,
  );

  useEffect(() => {
    if (!sopId || !versionInContext?.versionId || stepParam == null) return;
    fetchFeedbackForVersion(sopId, versionInContext.versionId);
  }, [sopId, versionInContext?.versionId, stepParam, fetchFeedbackForVersion]);

  const onOpenDraft = useCallback(
    async (versionId: string) => {
      if (!sopId || selectedVersion?.versionId === versionId) return;
      setIsVersionSwitchLoading(true);
      try {
        await fetchSOPById(sopId, versionId);
        refreshSelectedSOP();
        const next = new URLSearchParams(searchParams);
        next.set("versionId", versionId);
        setSearchParams(next, { replace: true });
      } catch (e) {
        console.error("Switch to draft failed:", e);
        showToast(
          "Error",
          "Could not switch to draft version. Please try again.",
          "error",
        );
      } finally {
        setIsVersionSwitchLoading(false);
      }
    },
    [
      sopId,
      selectedVersion?.versionId,
      fetchSOPById,
      refreshSelectedSOP,
      searchParams,
      setSearchParams,
      showToast,
    ],
  );

  const onCreateDraft = useCallback(async () => {
    if (!sopId) return;
    setIsCreatingNewDraft(true);
    try {
      await createNewDraft(sopId);
      refreshSelectedSOP();
      showToast(
        "New version created",
        "You can now edit the new draft version.",
        "success",
      );
    } catch (e) {
      console.error("Create new draft failed:", e);
      showToast(
        "Error",
        "Failed to create a new version. Please try again.",
        "error",
      );
    } finally {
      setIsCreatingNewDraft(false);
    }
  }, [sopId, createNewDraft, refreshSelectedSOP, showToast]);

  // When opening from feedback page with feedbackId in URL, preselect that feedback in the reference panel
  useEffect(() => {
    if (
      !sopId ||
      !versionInContext?.versionId ||
      !feedbackIdParam ||
      stepParam == null
    )
      return;
    const stepKey = `${sopId}:${versionInContext.versionId}:${stepParam}`;
    setSelectedFeedbackIdForStep(stepKey, feedbackIdParam);
  }, [
    sopId,
    versionInContext?.versionId,
    stepParam,
    feedbackIdParam,
    setSelectedFeedbackIdForStep,
  ]);

  useEffect(() => {
    if (!sopId || !versionInContext?.steps) return;
    const step = versionInContext.steps[index];
    if (step?.sourceVideoId && step?.sourceSegment) {
      setPreviewFileId(sopId, step.sourceVideoId);
      setFocusedSegment(sopId, {
        videoId: step.sourceVideoId,
        segment: step.sourceSegment,
      });
    } else {
      setFocusedSegment(sopId, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sopId,
    index,
    setFocusedSegment,
    setPreviewFileId,
    // currentPreviewVideoId,
    // currentPreviewSegmentStart,
    // currentPreviewSegmentEnd,
  ]);

  const stepSegments = useMemo(() => {
    const steps = versionInContext?.steps ?? [];
    return steps
      .map((s, index) => ({ step: s, index }))
      .filter(
        (
          x,
        ): x is {
          step: typeof x.step & {
            sourceVideoId: string;
            sourceSegment: NonNullable<typeof x.step.sourceSegment>;
          };
          index: number;
        } => !!x.step.sourceVideoId && !!x.step.sourceSegment,
      )
      .map(({ step: s }) => ({
        videoId: s.sourceVideoId!,
        segment: s.sourceSegment!,
        title: s.title,
      }));
  }, [versionInContext?.steps]);

  useEffect(() => {
    loadedImageIdsRef.current.clear();
    resetProcessedKeys();
  }, [sopId, versionInContext?.versionId, resetProcessedKeys]);

  const renderedSteps = useMemo(
    () => (reviewMode ? latestVersion?.steps ?? [] : versionInContext?.steps ?? []),
    [reviewMode, latestVersion?.steps, versionInContext?.steps],
  );

  const visibleStepIndices = useMemo(() => {
    if (renderedSteps.length === 0) return [];
    const min = Math.max(0, index - 1);
    const max = Math.min(renderedSteps.length - 1, index + 1);
    const indices: number[] = [];
    for (let i = min; i <= max; i += 1) {
      indices.push(i);
    }
    return indices;
  }, [index, renderedSteps.length]);

  // Load only images for visible steps (current step + neighbors).
  const loadVisibleStepImages = useCallback(async () => {
    if (renderedSteps.length === 0 || visibleStepIndices.length === 0) return;

    const allImageRefIds = visibleStepIndices
      .flatMap((visibleIndex) => {
        const step = renderedSteps[visibleIndex];
        if (!step) return [];
        const detailsRefs = extractImageReferences(step.details);
        const stepRefs = step.imageReferences || [];
        return [...stepRefs, ...detailsRefs];
      })
      .filter((id): id is string => {
        const isValid = Boolean(id) && isValidImageRefId(id);
        if (!isValid && id) {
          console.error("Invalid image reference ID:", id);
        }
        return isValid;
      });

    const uniqueImageRefIds = [
      ...new Set(allImageRefIds.map(normalizeImageRefId)),
    ].filter((id) => id !== "");

    const newImageIds = uniqueImageRefIds.filter(
      (id) => !loadedImageIdsRef.current.has(id) && !imageUrls[id],
    );

    if (newImageIds.length === 0) return;

    setIsLoadingImages(true);
    try {
      newImageIds.forEach((id) => loadedImageIdsRef.current.add(id));
      const urls = await getImageUrls(newImageIds);
      setImageUrls((prev) => ({ ...prev, ...urls }));
    } catch (error) {
      console.error("Error loading images:", error);
    } finally {
      setIsLoadingImages(false);
    }
  }, [
    renderedSteps,
    visibleStepIndices,
    getImageUrls,
    imageUrls,
    isValidImageRefId,
    normalizeImageRefId,
    setImageUrls,
    setIsLoadingImages,
  ]);

  useEffect(() => {
    loadVisibleStepImages();
  }, [loadVisibleStepImages]);

  const versionForExport = selectedVersion ?? latestVersion;
  const soapOutput: StatementOfOperationsOutput | null =
    selectedSOP && versionForExport
      ? selectedSOP.toStatementOfOperationsOutput(versionForExport)
      : null;
  const handlePdfDownloadError = useCallback(
    (message: string) => {
      showToast("Download failed", message, "error");
    },
    [showToast],
  );
  const handleMarkdownDownloadError = useCallback(
    (message: string) => {
      showToast("Download failed", message, "error");
    },
    [showToast],
  );
  const { downloading, downloadPdf } = usePdfGenerate(
    soapOutput,
    handlePdfDownloadError,
  );
  const { exportingMarkdown, downloadMarkdown } = useMarkdownExport(
    soapOutput,
    handleMarkdownDownloadError,
  );
  const stepsLength = versionInContext?.steps.length || 0;
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const selectedFeedbackIdByStep = useFeedbackStore((s) => s.selectedFeedbackIdByStep);
  const sopTitleKey = sopId && versionInContext?.versionId
    ? `${sopId}:${versionInContext.versionId}:${TITLE_PAGE_FEEDBACK_STEP_INDEX}`
    : null;
  const sopTitleHighlighted = sopTitleKey ? (selectedFeedbackIdByStep[sopTitleKey] ?? null) !== null : false;
  const [isRegeneratingTitle, setIsRegeneratingTitle] = useState(false);
  const [startReviewOpen, setStartReviewOpen] = useState(false);
  const [exitReviewConfirmOpen, setExitReviewConfirmOpen] = useState(false);
  const [isSavingCandidates, setIsSavingCandidates] = useState(false);
  const [isApplyingDecisions, setIsApplyingDecisions] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [_feedbackModeEnabled, setFeedbackModeEnabled] = useState(false);

  const reviewedCount = candidates.filter(
    (c) => decisions[c.stepIndex] !== undefined,
  ).length;
  const totalCandidates = candidates.length;
  const allDecided =
    totalCandidates > 0 &&
    candidates.every((c) => decisions[c.stepIndex] !== undefined);

  useEffect(() => {
    if (candidates.length === 0) hasWarnedEditWithCandidatesRef.current = false;
  }, [candidates.length]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "Escape") setFeedbackModeEnabled(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasReviewUnsaved =
        reviewMode && candidates.length > 0 && candidatesDirty;
      if (!hasUnsavedChanges && !hasReviewUnsaved) return;
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges, reviewMode, candidates.length, candidatesDirty]);

  // Fetch existing regeneration candidates on mount; auto-enter review mode if any exist.
  // Guarded so we only hit the backend once per (sopId, versionId) pair even under StrictMode re-renders.
  useEffect(() => {
    const targetVersionId =
      versionInContext?.versionId ?? latestVersion?.versionId;
    if (!sopId || !targetVersionId) return;

    if (
      loadedCandidatesForRef.current &&
      loadedCandidatesForRef.current.sopId === sopId &&
      loadedCandidatesForRef.current.versionId === targetVersionId
    ) {
      return;
    }

    sopService
      .getRegenerationCandidates(sopId, targetVersionId)
      .then((res) => {
        loadedCandidatesForRef.current = {
          sopId,
          versionId: targetVersionId,
        };
        if (res.candidates?.length) {
          setCandidates(res.candidates);
          setReviewMode(true);
          // Re-fetch feedback so the suggestions tab shows up-to-date resolved/open
          // statuses — the admin may have resolved candidate feedback while regenerating.
          fetchFeedbackForVersion(sopId, targetVersionId);
          const firstStepWithCandidate = res.candidates[0]?.stepIndex;
          if (
            typeof firstStepWithCandidate === "number" &&
            firstStepWithCandidate >= 0
          ) {
            setIndex(firstStepWithCandidate);
          }
        }
      })
      .catch(() => {
        loadedCandidatesForRef.current = {
          sopId,
          versionId: targetVersionId,
        };
      });
  }, [
    sopId,
    versionInContext?.versionId,
    latestVersion?.versionId,
    setCandidates,
    setReviewMode,
    setIndex,
    fetchFeedbackForVersion,
  ]);

  const handleDownloadWithValidation = async (
    downloadFn: () => Promise<void>,
  ) => {
    if (!versionInContext?.titlePage) {
      showToast(
        "Download blocked: save Cover Page first",
        "You opened the SOP without saving the Cover Page. Go to Title Page, click Save Changes, then try Download PDF or Download Markdown again.",
        "warning",
      );
      return;
    }

    await downloadFn();
  };

  const handleTitleEdit = async (newTitle: string) => {
    if (!selectedSOP || !sopId) return;

    try {
      await updateSopTitleById(sopId, newTitle);
      setIsEditingTitle(false);
      showToast(
        "Title Updated",
        "SOP title has been successfully updated.",
        "success",
      );
    } catch (error) {
      console.error("Error updating SOP title:", error);
      showToast(
        "Error",
        "Failed to update SOP title. Please try again.",
        "error",
      );
    }
  };

  const handleTitleCancel = () => {
    setIsEditingTitle(false);
  };

  /** Navigate away from steps; if there are unsaved step changes, show confirm modal first. */
  const requestNavigation = useCallback(
    (path: string, state?: object) => {
      if (hasUnsavedChanges && isEditable && canEdit) {
        setPendingNavigation({ path, state });
        setLeaveStepChangesConfirmOpen(true);
      } else {
        navigate(path, state != null ? { state } : undefined);
      }
    },
    [hasUnsavedChanges, isEditable, canEdit, navigate],
  );

  /** Discard in-memory step edits (refetch from server), clear unsaved flag, then run pending navigation. */
  const discardStepChangesAndNavigate = useCallback(async () => {
    if (!sopId || !pendingNavigation) return;
    let versionFromPath: string | undefined;
    try {
      const u = new URL(pendingNavigation.path, window.location.origin);
      versionFromPath = u.searchParams.get("versionId") ?? undefined;
    } catch {
      versionFromPath = undefined;
    }
    if (versionFromPath) {
      await fetchSOPById(sopId, versionFromPath);
    } else {
      await fetchSOPById(sopId);
    }
    refreshSelectedSOP();
    const steps = getSopById(sopId)?.getLatestVersion()?.steps;
    lastSavedStepsRef.current = steps ? steps.map((s) => ({ ...s })) : null;
    setHasUnsavedChanges(false);
    navigate(
      pendingNavigation.path,
      pendingNavigation.state != null
        ? { state: pendingNavigation.state }
        : undefined,
    );
    setPendingNavigation(null);
    setLeaveStepChangesConfirmOpen(false);
  }, [
    sopId,
    pendingNavigation,
    fetchSOPById,
    refreshSelectedSOP,
    getSopById,
    setHasUnsavedChanges,
    navigate,
  ]);

  const handleNavigateToTitlePage = () => {
    if (!sopId) return;
    const vid = versionInContext?.versionId ?? latestVersion?.versionId ?? "";
    const path =
      vid !== ""
        ? `/sop/${sopId}?versionId=${encodeURIComponent(vid)}`
        : `/sop/${sopId}`;
    requestNavigation(path, { fromStepView: true });
  };

  const handleRegenerateTitle = async () => {
    if (!sopId || isRegeneratingTitle) return;

    setIsRegeneratingTitle(true);
    try {
      const response = await sopService.regenerateTitle(sopId);
      if (response.version_title) {
        await updateSopTitleById(sopId, response.version_title);
        showToast(
          "Title Regenerated",
          "SOP title has been regenerated with AI.",
          "success",
        );
      } else {
        showToast(
          "Error",
          "No title was generated. Please try again.",
          "error",
        );
      }
    } catch (error) {
      console.error("Error regenerating SOP title:", error);
      showToast(
        "Error",
        "Failed to regenerate SOP title. Please try again.",
        "error",
      );
    } finally {
      setIsRegeneratingTitle(false);
    }
  };

  const handleCompleteVersion = async () => {
    const description = completeVersionDescription.trim();
    if (!sopId || !latestVersion) {
      showToast("Error", "No version to complete.", "warning");
      return;
    }
    if (!description) {
      showToast(
        "Description required",
        "Enter a description for the version being completed.",
        "warning",
      );
      return;
    }
    setIsCompletingVersion(true);
    try {
      const { completed } = await completeVersion(
        sopId,
        latestVersion.versionId,
        {
          sop_description: description,
        },
      );
      track("sop_version_completed", {
        sop_id: sopId,
        version_id: completed.versionId,
      });
      setCompleteVersionOpen(false);
      setCompleteVersionDescription("");

      if (releaseThenDownloadMode) {
        setReleaseThenDownloadMode(false);
        pendingPostReleaseNavigateRef.current = { completed, sopId };
        setDownloadFormatPickerOpen(true);
      } else {
        await fetchSOPs();
        await fetchSOPById(sopId, completed.versionId);
        setSelectedVersion(completed);
        navigate(
          `/sop/${sopId}?versionId=${encodeURIComponent(completed.versionId)}`,
        );
        showToast(
          "Version released",
          "You are viewing the released version on the title page.",
          "success",
        );
      }
    } catch (error: unknown) {
      console.error("Complete version failed:", error);
      const message =
        error && typeof error === "object" && "response" in error
          ? ((error as { response?: { data?: { message?: string } } }).response
              ?.data?.message ?? "")
          : "";
      const isSopDescriptionError =
        typeof message === "string" &&
        message.toLowerCase().includes("sop_description");
      showToast(
        "Error",
        isSopDescriptionError
          ? "A description is required when completing the version."
          : error instanceof Error
            ? error.message
            : "Failed to complete version.",
        "error",
      );
    } finally {
      setIsCompletingVersion(false);
    }
  };

  const handleStepChange = (stepIndex: number, updatedStep: Step) => {
    // Update the store directly instead of using local state
    const updatedSop = getSelectedSOP();
    if (!updatedSop) return;

    const latestVersion = updatedSop.getLatestVersion();
    if (!latestVersion) return;

    const currentStep = latestVersion.steps[stepIndex];
    if (currentStep == null) return;

    // Normalize store step same as StepEditor (ensurePartIds/ensureToolIds) so
    // normalization-only differences don't set hasUnsavedChanges
    const normalizedCurrent: Step = {
      ...currentStep,
      parts: ensurePartIds(currentStep.parts, stepIndex),
      tools: ensureToolIds(currentStep.tools, stepIndex),
    };

    // Normalize details HTML for comparison so TipTap re-serialization on load/refresh
    // (whitespace, editor-only attributes, empty tags, HTML entities) doesn't trigger hasUnsavedChanges.
    // Keep in sync with baseline comparison below (lastSavedStepsRef) so "type then erase" clears the Save button.
    const normalizeDetailsForCompare = (html: string) => {
      let s = (html || "").trim().replace(/\s+/g, " ");
      s = s.replace(/\s*contenteditable\s*=\s*["'][^"']*["']/gi, "");
      s = s.replace(/\s*data-node-type\s*=\s*["'][^"']*["']/gi, "");
      s = s.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, "<p></p>");
      // Decode HTML entities so store (raw &) and editor (&#38; / &amp;) compare equal
      s = s
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, "&");
      return s;
    };

    const currentForCompare = {
      ...normalizedCurrent,
      details: normalizeDetailsForCompare(normalizedCurrent.details ?? ""),
    };
    const updatedForCompare = {
      ...updatedStep,
      details: normalizeDetailsForCompare(updatedStep.details ?? ""),
    };

    // Only track as unsaved change if the step actually changed
    const hasRealChange =
      JSON.stringify(currentForCompare) !== JSON.stringify(updatedForCompare);

    if (!hasRealChange) {
      return;
    }

    const newSteps = [...latestVersion.steps];
    newSteps[stepIndex] = updatedStep;

    // Update the store with the new steps
    useSopStore.setState((state) => ({
      sops: state.sops.map((s) => {
        if (s.sopId === updatedSop.sopId) {
          const updatedVersions = s.versions.map((v) => {
            if (v.versionId === latestVersion.versionId) {
              return { ...v, steps: newSteps };
            }
            return v;
          });
          return new SOP(
            s.sopId,
            s.sopTitle,
            updatedVersions,
            s.createdAt,
            s?.processingStatus,
            s?.processingTimeSeconds,
            s.bom,
            s.revisions,
          );
        }
        return s;
      }),
    }));

    // Compare to last-saved baseline so "type then erase" clears the Save button
    const lastSaved = lastSavedStepsRef.current;
    if (lastSaved != null && lastSaved.length === newSteps.length) {
      const norm = (step: Step, i: number) => ({
        ...step,
        parts: ensurePartIds(step.parts, i),
        tools: ensureToolIds(step.tools, i),
        details: normalizeDetailsForCompare(step.details ?? ""),
      });
      const normalizedNew = newSteps.map(norm);
      const normalizedLast = lastSaved.map(norm);
      setHasUnsavedChanges(
        JSON.stringify(normalizedNew) !== JSON.stringify(normalizedLast),
      );
    } else {
      setHasUnsavedChanges(true);
    }

    // Warn once when editing while candidates are pending.
    // This warning is only applicable to super-admins (they edit candidates directly);
    // for regular editors/owners, step changes for unlocked steps remain usable.
    if (
      isSuperAdmin &&
      candidates.length > 0 &&
      !hasWarnedEditWithCandidatesRef.current
    ) {
      hasWarnedEditWithCandidatesRef.current = true;
      showToast(
        "AI versions pending review",
        "Your edits are kept locally, but step changes won't be saved until you accept or request changes for the AI versions.",
        "warning",
      );
    }
  };

  const handleCreateStep = async (idx: number) => {
    if (!isEditable) return;
    if (hasLockedSteps || isRegenerationRequested) {
      showToast(
        "Update in progress",
        "You can't add/reorder/delete steps while updates are being prepared.",
        "warning",
      );
      return;
    }
    try {
      await addStep(idx);
      showToast(
        "Step Added",
        "New step has been successfully added.",
        "success",
      );
      setIndex(idx); // Navigate to the new step
    } catch (error) {
      console.error("Error adding step:", error);
      showToast("Error", "Failed to add step. Please try again.", "error");
    }
  };

  const handleDeleteStep = async (stepIndex: number) => {
    const stepsLength = latestVersion?.steps.length || 0;

    if (hasLockedSteps || isRegenerationRequested) {
      showToast(
        "Update in progress",
        "You can't add/reorder/delete steps while updates are being prepared.",
        "warning",
      );
      return;
    }

    // Don't allow deleting if there's only one step
    if (stepsLength <= 1) {
      showToast("Cannot Delete", "At least one step is required.", "warning");
      return;
    }

    try {
      await deleteStep(stepIndex);

      // Adjust the current index after deletion
      if (stepIndex === index) {
        // If we deleted the current step, go to the previous one or first one
        setIndex(Math.max(0, stepIndex - 1));
      } else if (stepIndex < index) {
        // If we deleted a step before the current one, adjust the index
        setIndex(index - 1);
      }

      showToast(
        "Step Deleted",
        "Step has been successfully deleted.",
        "success",
      );
    } catch (error) {
      console.error("Error deleting step:", error);
      showToast("Error", "Failed to delete step. Please try again.", "error");
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!isEditable) return;
    if (reviewMode && candidates.length > 0) return;
    if (hasLockedSteps || isRegenerationRequested) return;
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = parseInt(active.id as string);
      const newIndex = parseInt(over.id as string);

      console.log("Drag end:", {
        activeId: active.id,
        overId: over.id,
        oldIndex,
        newIndex,
      });

      try {
        await reorderSteps(oldIndex, newIndex);

        // Force refresh the selected SOP to ensure UI updates
        refreshSelectedSOP();

        // Update the current index if the dragged step was the active one
        if (index === oldIndex) {
          setIndex(newIndex);
        } else if (index > oldIndex && index <= newIndex) {
          // If we moved a step up, adjust the current index
          setIndex(index - 1);
        } else if (index < oldIndex && index >= newIndex) {
          // If we moved a step down, adjust the current index
          setIndex(index + 1);
        }

        showToast(
          "Step Moved",
          "Step has been reordered successfully.",
          "success",
        );
      } catch (error) {
        console.error("Error reordering step:", error);
        showToast(
          "Error",
          "Failed to reorder step. Please try again.",
          "error",
        );
      }
    }
  };

  const handleSaveAllChanges = async (): Promise<boolean> => {
    // Get the current steps from the store instead of using the potentially stale latestVersion prop
    const currentSop = getSelectedSOP();
    const currentLatestVersion = currentSop?.getLatestVersion();

    if (!currentLatestVersion?.steps || !sopId) return false;

    try {
      // Process cropped and annotated images in all steps before saving
      const processedSteps = await Promise.all(
        currentLatestVersion.steps.map(async (step, stepIdx) => {
          if (!step.details) return step;
          // Keep regeneration-locked steps stable.
          // Backend validates locked step content, so we must not reprocess them.
          if (lockedStepSet.has(stepIdx)) return step;

          try {
            let processedDetails = await processCroppedImages(
              step.details,
              sopId,
              getImageUrls,
            );
            processedDetails = await processAnnotatedImages(
              processedDetails,
              sopId,
              getImageUrls,
            );
            return {
              ...step,
              details: processedDetails,
            };
          } catch (error) {
            console.error("Error processing images for step:", error);
            return step; // Return original step if processing fails
          }
        }),
      );

      const result = await saveSOPChanges(processedSteps);
      if (result.success) {
        lastSavedStepsRef.current = processedSteps.map((s) => ({ ...s }));
        showToast(
          "Changes Saved",
          "All changes have been successfully saved.",
          "success",
        );
        return true;
      }
      if (result.versionConflict) {
        showToast(
          "Version out of date",
          "The SOP was changed elsewhere. We loaded the latest version. Make your edits and save again.",
          "warning",
        );
        return false;
      }
      showToast(
        "Error",
        result.error ?? "Failed to save changes. Please try again.",
        "error",
      );
      return false;
    } catch (error) {
      console.error("Error saving changes:", error);
      showToast("Error", "Failed to save changes. Please try again.", "error");
      return false;
    }
  };

  const handleFinishVerification = async () => {
    if (!sopId || isVerifying) return;
    if (hasUnsavedChanges) {
      showToast(
        "Unsaved Changes",
        "Please save your changes before finishing verification.",
        "warning",
      );
      return;
    }
    setIsVerifying(true);
    try {
      if (
        isSuperAdmin &&
        reviewMode &&
        candidates.length > 0 &&
        latestVersion?.versionId
      ) {
        const result = await saveCandidates({
          sopId,
          versionId: latestVersion.versionId,
          notifyOwner: true,
        });
        if (!result.success) {
          showToast(
            "Error",
            result.error ?? "Failed to save and send AI versions.",
            "error",
          );
          return;
        }
        showToast("Sent", "AI versions saved and owner notified.", "success");
      }
      await api.put(`/sops/${sopId}/verify`);
      showToast(
        "Success",
        "SOP verification completed successfully.",
        "success",
      );
      navigate("/sops/pending-verification");
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { data?: { message?: string; error?: string } };
      };
      const msg =
        axiosError?.response?.data?.message ??
        axiosError?.response?.data?.error ??
        "Failed to verify SOP. Please try again.";
      showToast("Error", msg, "error");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSendCandidatesToOwner = async () => {
    if (!sopId || isSendingCandidates) return;
    if (!isSuperAdmin || !reviewMode) return;
    if (selectedSOP?.processingStatus !== ProcessingStatusEnum.REGENERATE)
      return;
    if (!latestVersion?.versionId) return;
    if (candidates.length === 0) return;

    setIsSendingCandidates(true);
    try {
      if (hasUnsavedChanges) {
        const saved = await handleSaveAllChanges();
        if (!saved) return;
      }
      const result = await saveCandidates({
        sopId,
        versionId: latestVersion.versionId,
        notifyOwner: true,
      });
      if (!result.success) {
        showToast(
          "Error",
          result.error ?? "Failed to save and notify owner.",
          "error",
        );
        return;
      }
      showToast("Sent", "AI versions saved and owner notified.", "success");
      navigate("/sops/pending-verification");
    } finally {
      setIsSendingCandidates(false);
    }
  };

  // Handle case where SOP is found but latestVersion is null (direct navigation)
  useEffect(() => {
    if (selectedSOP && !latestVersion) {
      refreshSelectedSOP();
    }
  }, [selectedSOP, latestVersion, refreshSelectedSOP]);

  if (
    selectedSOP?.processingStatus ===
      ProcessingStatusEnum.PENDING_VERIFICATION &&
    !isSuperAdmin
  ) {
    return (
      <div className="sop-editor">
        <Header />
        <SopAccessDenied
          message="This SOP is still being prepared. Please try again later."
          showHeader={false}
        />
      </div>
    );
  }

  return (
    <>
      <div className="sop-editor">
        <Header />

        {!selectedSOP && sopError ? (
          <SopAccessDenied message={sopError} showHeader={false} />
        ) : isLoading && !selectedSOP ? (
          <div className="loading-state">
            <div className="loading-content">
              <div className="loading-spinner">
                <Loader className="animate-spin w-12 h-12 text-blue-400" />
              </div>
              <h2 className="loading-title">Loading SOP...</h2>
              <p className="loading-description">
                Please wait while we fetch your SOP data.
              </p>
            </div>
          </div>
        ) : selectedSOP ? (
          <div className="editor-layout editor-layout-with-bar">
            <div className="editor-layout">
              {/* Left Sidebar - collapsible SOP Navigation */}
              <div
                className={`sop-editor-sidebar ${sidebarCollapsed ? "sop-editor-sidebar--collapsed" : ""}`}
              >
                <div className="sop-editor-sidebar-header">
                  <button
                    type="button"
                    className="sop-editor-sidebar-toggle"
                    onClick={() => setSidebarCollapsed((c) => !c)}
                    title={
                      sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
                    }
                    aria-label={
                      sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
                    }
                  >
                    {sidebarCollapsed ? (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronLeft className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                  {!sidebarCollapsed &&
                    candidates.length === 0 &&
                    isEditable &&
                    canEdit &&
                    !isRegenerationRequested && (
                      <button
                        type="button"
                        className="sop-editor-sidebar-upload-btn"
                        onClick={() => setStartReviewOpen(true)}
                        disabled={
                          !latestVersion?.steps?.length ||
                          hasLockedSteps ||
                          isRegenerationRequested
                        }
                        title={
                          !latestVersion?.steps?.length
                            ? "No steps to update yet"
                            : isRegenerationRequested
                              ? "Regeneration is currently in progress"
                              : hasLockedSteps
                                ? "An update is already in progress"
                                : "Upload files and regenerate steps with AI"
                        }
                        aria-label="Update SOP"
                      >
                        <Upload className="w-4 h-4 shrink-0" />
                        <span className="sop-editor-sidebar-upload-btn-label">
                          Update SOP
                        </span>
                      </button>
                    )}
                  {!sidebarCollapsed &&
                    candidates.length === 0 &&
                    isEditable &&
                    canEdit &&
                    isRegenerationRequested &&
                    !ownerCanReview && (
                      <div className="text-[11px] px-3 py-2 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 backdrop-blur mt-2">
                        We are preparing a new version for you...
                      </div>
                    )}
                  {!sidebarCollapsed && candidates.length > 0 && (
                    <button
                      type="button"
                      className={`sop-editor-sidebar-upload-btn flex-1 min-w-0 ${reviewMode ? "sop-editor-sidebar-review-btn--exit" : "sop-editor-sidebar-review-btn--enter"}`}
                      disabled={isSuperAdminWaitingForOwnerReview}
                      title={
                        isSuperAdminWaitingForOwnerReview
                          ? "Waiting for candidate review"
                          : reviewMode
                            ? "Exit Review mode"
                            : "Review AI Versions"
                      }
                      onClick={() => {
                        if (isSuperAdminWaitingForOwnerReview) return;
                        if (reviewMode && candidatesDirty) {
                          setExitReviewConfirmOpen(true);
                        } else {
                          setReviewMode(!reviewMode);
                        }
                      }}
                    >
                      <ClipboardCheck className="w-4 h-4 shrink-0" />
                      <span className="sop-editor-sidebar-upload-btn-label">
                        {reviewMode ? "Exit Review" : "Review AI Versions"}
                      </span>
                    </button>
                  )}
                </div>

                <div className="sop-editor-sidebar-scroll">
                  <div className="sop-editor-sidebar-section">
                    {!sidebarCollapsed && (
                      <h3 className="sop-editor-sidebar-title">
                        SOP Navigation
                      </h3>
                    )}
                    <div className="step-list">
                      {/* Title Page – not draggable, first in list */}
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={handleNavigateToTitlePage}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleNavigateToTitlePage();
                          }
                        }}
                        className={`sop-editor-sidebar-item sop-editor-sidebar-item--title ${sidebarCollapsed ? "sop-editor-sidebar-item--collapsed" : ""}`}
                        title="Title Page"
                      >
                        {sidebarCollapsed ? (
                          <FileText className="w-5 h-5 text-slate-300 shrink-0" />
                        ) : (
                          <>
                            <div className="sop-editor-sidebar-item-icon sop-editor-sidebar-item-icon--title">
                              <FileText className="w-4 h-4" />
                            </div>
                            <span className="sop-editor-sidebar-item-label">
                              Title Page
                            </span>
                          </>
                        )}
                      </div>

                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={
                            versionInContext?.steps.map((_, index) =>
                              index.toString(),
                            ) || []
                          }
                          strategy={verticalListSortingStrategy}
                        >
                          {versionInContext?.steps.map((step, stepIndex) => {
                            const hasCandidate = candidates.some(
                              (c) => c.stepIndex === stepIndex,
                            );
                            const isLocked = lockedStepSet.has(stepIndex);
                            const stepNavStatus = getStepNavStatus({
                              hasCandidate,
                              isLocked,
                              reviewMode,
                              decision: decisions[stepIndex],
                              viewerIsSuperAdmin: isSuperAdmin,
                              superAdminWaitingForOwnerReview:
                                isSuperAdminWaitingForOwnerReview,
                            });
                            const statusClass = `sop-editor-sidebar-step-num--status-${stepNavStatusToSuffix(stepNavStatus)}`;

                            return sidebarCollapsed ? (
                              <button
                                key={stepIndex}
                                type="button"
                                className={`sop-editor-sidebar-step-num ${stepIndex === index ? "sop-editor-sidebar-step-num--active" : ""} ${statusClass}`}
                                onClick={() => setIndex(stepIndex)}
                                disabled={false}
                                title={
                                  getStepNavStatusTitle(stepNavStatus)
                                    ? `${step.title} — ${getStepNavStatusTitle(stepNavStatus)}`
                                    : step.title
                                }
                                aria-label={getStepNavStatusAriaLabel(
                                  stepNavStatus,
                                  step.title,
                                  stepIndex + 1,
                                )}
                              >
                                {stepIndex + 1}
                              </button>
                            ) : (
                              <SortableStepItem
                                key={stepIndex}
                                step={step}
                                stepIndex={stepIndex}
                                isActive={stepIndex === index}
                                isEditable={isEditable && canEdit}
                                onStepClick={setIndex}
                                canEdit={canEdit}
                                status={stepNavStatus}
                                isReorderDisabled={
                                  !isEditable ||
                                  !canEdit ||
                                  hasLockedSteps ||
                                  (reviewMode && candidates.length > 0)
                                }
                                isStepDisabled={false}
                              />
                            );
                          })}
                        </SortableContext>
                      </DndContext>
                      {canEdit && isEditable && !hasLockedSteps && (
                        <button
                          type="button"
                          className={
                            sidebarCollapsed
                              ? "sop-editor-sidebar-add-step sop-editor-sidebar-add-step--collapsed"
                              : "sop-editor-sidebar-add-step w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-slate-600 text-slate-400 hover:border-cyan-400 hover:text-cyan-400 cursor-pointer hover:bg-cyan-400/5 hover:-translate-y-0.5 transition-all duration-200"
                          }
                          title="Add step"
                          aria-label="Add step"
                          onClick={() =>
                            handleCreateStep(
                              versionInContext?.steps.length || 0,
                            )
                          }
                        >
                          {sidebarCollapsed ? (
                            <Plus className="w-5 h-5 shrink-0" aria-hidden />
                          ) : (
                            <>
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                />
                              </svg>
                              <span>Add Step</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {reviewMode &&
                  candidates.length > 0 &&
                  showApproveDenyActions && (
                    <div
                      className={`sop-editor-sidebar-review-footer ${sidebarCollapsed ? "sop-editor-sidebar-review-footer--collapsed" : ""}`}
                    >
                      <div className="sop-editor-sidebar-review-progress-label">
                        <span>Review progress</span>
                        <span>
                          {reviewedCount}/{totalCandidates} reviewed
                        </span>
                      </div>
                      <div
                        className="sop-editor-sidebar-review-progress-track"
                        role="progressbar"
                        aria-valuenow={reviewedCount}
                        aria-valuemin={0}
                        aria-valuemax={totalCandidates}
                        aria-label={`Steps reviewed: ${reviewedCount} of ${totalCandidates}`}
                      >
                        <div
                          className="sop-editor-sidebar-review-progress-fill"
                          style={{
                            width: `${
                              totalCandidates > 0
                                ? (reviewedCount / totalCandidates) * 100
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                      <Tooltip.Provider delayDuration={250}>
                        <Tooltip.Root>
                          <Tooltip.Trigger asChild>
                            <span className="inline-flex w-full">
                              <button
                                type="button"
                                className={`sop-editor-sidebar-finalize-btn ${sidebarCollapsed ? "sop-editor-sidebar-finalize-btn--collapsed" : ""}`}
                                onClick={async () => {
                                  if (
                                    !sopId ||
                                    !latestVersion?.versionId ||
                                    !latestVersion?.steps
                                  )
                                    return;
                                  const candidatesSnapshot = [...candidates];
                                  const decisionsSnapshot = { ...decisions };
                                  const versionIdApplied =
                                    latestVersion.versionId;
                                  setIsApplyingDecisions(true);
                                  const result = await applyCandidateDecisions({
                                    sopId,
                                    versionId: versionIdApplied,
                                    currentSteps: latestVersion.steps,
                                  });
                                  setIsApplyingDecisions(false);
                                  if (result.success) {
                                    const versionKey = `${sopId}:${versionIdApplied}`;
                                    const feedbackList =
                                      useFeedbackStore.getState()
                                        .feedbackByVersion[versionKey] ?? [];
                                    for (const c of candidatesSnapshot) {
                                      if (
                                        decisionsSnapshot[c.stepIndex] ===
                                          "accept" &&
                                        c.id
                                      ) {
                                        for (const fb of feedbackList.filter(
                                          (f) => f.candidateId === c.id,
                                        )) {
                                          try {
                                            await deleteFeedback(sopId, fb.id);
                                          } catch {
                                            /* non-fatal */
                                          }
                                        }
                                      }
                                    }
                                    await fetchFeedbackForVersion(
                                      sopId,
                                      versionIdApplied,
                                    );
                                    Object.entries(decisionsSnapshot).forEach(
                                      ([stepIdx, decision]) => {
                                        track(
                                          "sop_proposed_version_responded",
                                          {
                                            sop_id: sopId,
                                            user_decision:
                                              decision === "accept"
                                                ? "accepted"
                                                : "rejected",
                                            step_index: Number(stepIdx),
                                          },
                                        );
                                      },
                                    );
                                    setReviewMode(false);
                                    await fetchSOPById(sopId);
                                    refreshSelectedSOP();
                                    setLockedStepIndices([]);
                                    setOwnerCanReview(false);
                                    setOwnerVisibleAny(false);
                                    await refreshLocks();
                                    const changeRequestCount =
                                      result.changeRequestCount ?? 0;
                                    showToast(
                                      "Done",
                                      changeRequestCount > 0
                                        ? `Your choices have been applied. ${changeRequestCount} step${changeRequestCount === 1 ? "" : "s"} sent back to the pipeline for rework.`
                                        : "Your choices have been applied.",
                                      "success",
                                    );
                                  } else {
                                    track("app_error_occurred", {
                                      error_type: result.versionConflict
                                        ? "version_conflict"
                                        : "apply_decisions_failed",
                                      error_location: "proposal_generation",
                                      severity: "medium",
                                    });
                                    showToast(
                                      "Error",
                                      result.error ?? "Failed to apply.",
                                      "error",
                                    );
                                    if (result.versionConflict) {
                                      refreshSelectedSOP();
                                      await refreshLocks();
                                    }
                                  }
                                }}
                                disabled={!allDecided || isApplyingDecisions}
                                aria-label={
                                  sidebarCollapsed
                                    ? `Done — ${reviewedCount} of ${totalCandidates} steps reviewed`
                                    : undefined
                                }
                              >
                                {isApplyingDecisions ? (
                                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                                ) : sidebarCollapsed ? (
                                  <CircleCheck
                                    className="w-5 h-5 shrink-0"
                                    aria-hidden
                                  />
                                ) : null}
                                {!sidebarCollapsed && <span>Done</span>}
                              </button>
                            </span>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content
                              className="sop-editor-sidebar-done-tooltip z-[100] max-w-[min(18rem,calc(100vw-2rem))] rounded-lg border border-slate-600 bg-slate-800 px-3 py-2.5 text-left text-xs text-slate-100 shadow-lg"
                              side="right"
                              sideOffset={10}
                            >
                              <p className="font-semibold text-slate-50">
                                {allDecided
                                  ? "All steps reviewed"
                                  : "Review progress"}
                              </p>
                              <p className="mt-1.5 leading-relaxed text-slate-300">
                                {allDecided
                                  ? "Your review will be applied to the SOP when you click Done."
                                  : `${reviewedCount} of ${totalCandidates} steps reviewed. Accept or request changes for every AI version before you can finish.`}
                              </p>
                              <Tooltip.Arrow className="fill-slate-800" />
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      </Tooltip.Provider>
                    </div>
                  )}
                {reviewMode &&
                  candidates.length > 0 &&
                  isSuperAdmin &&
                  selectedSOP?.processingStatus ===
                    ProcessingStatusEnum.REGENERATE && (
                    <div
                      className={`sop-editor-sidebar-review-footer ${sidebarCollapsed ? "sop-editor-sidebar-review-footer--collapsed" : ""}`}
                    >
                      {!sidebarCollapsed && (
                        <p className="text-xs text-slate-400 mb-3 leading-relaxed">
                          Send AI proposals to org editors for review.
                        </p>
                      )}
                      {isSendingCandidates ? (
                        <div className="flex items-center justify-center gap-2 py-2.5 text-slate-400 text-sm">
                          <Loader className="w-4 h-4 animate-spin shrink-0" />
                          {!sidebarCollapsed && <span>Sending...</span>}
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={`sop-editor-sidebar-submit-review-btn ${sidebarCollapsed ? "sop-editor-sidebar-submit-review-btn--collapsed" : ""}`}
                          onClick={() => void handleSendCandidatesToOwner()}
                          disabled={!latestVersion?.versionId}
                          title={
                            !latestVersion?.versionId
                              ? "No version loaded"
                              : "Submit proposed AI versions for owner review"
                          }
                          aria-label="Submit for review"
                        >
                          <ClipboardCheck
                            className={`${sidebarCollapsed ? "w-5 h-5" : "w-4 h-4"} shrink-0`}
                            aria-hidden
                          />
                          {!sidebarCollapsed && <span>Submit for Review</span>}
                        </button>
                      )}
                    </div>
                  )}
              </div>

              {/* Main Content Area */}
              <div className="main-content">
                {isViewingDoneVersion && (
                  <div className="flex pt-16">
                    <ReleasedVersionBanner
                      selectedVersion={selectedVersion}
                      hasDraftVersion={hasDraftVersion}
                      draftVersionId={draftVersionId}
                      hasSopId={Boolean(sopId)}
                      versionSwitchLoading={isVersionSwitchLoading}
                      isCreatingNewDraft={isCreatingNewDraft}
                      onOpenDraft={onOpenDraft}
                      onCreateDraft={onCreateDraft}
                    />
                  </div>
                )}
                {/* SOP Header */}
                <div className="sop-header">
                  <div className="sop-info">
                    <div className="sop-title-section">
                      {isEditingTitle && canEdit ? (
                        <InputField
                          value={displayTitle || ""}
                          onSave={handleTitleEdit}
                          onCancel={handleTitleCancel}
                          className="text-2xl font-bold bg-transparent border border-gray-300 rounded px-2 py-1 text-white w-full"
                          placeholder="Enter SOP title..."
                          autoFocus={true}
                        />
                      ) : (
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-3 group">
                            <h1
                              className={`sop-title transition-colors duration-200 ${isEditable && canEdit ? "cursor-pointer hover:text-blue-400" : "cursor-default"}${sopTitleHighlighted ? " ring-2 ring-blue-400 ring-offset-1 ring-offset-slate-900 rounded" : ""}`}
                              onClick={() =>
                                isEditable && canEdit && setIsEditingTitle(true)
                              }
                              title={
                                isEditable && canEdit
                                  ? "Click to edit title"
                                  : undefined
                              }
                            >
                              {displayTitle || ""}
                            </h1>
                            {sopId && versionInContext?.versionId && (
                              <FieldFeedbackButton
                                sopId={sopId}
                                sopTitle={selectedSOP?.sopTitle ?? ""}
                                versionId={versionInContext.versionId}
                                stepIndex={TITLE_PAGE_FEEDBACK_STEP_INDEX}
                                fieldLabel="SOP Title"
                              />
                            )}
                            {isEditable && canEdit && (
                              <>
                                <button
                                  onClick={handleRegenerateTitle}
                                  disabled={isRegeneratingTitle}
                                  className="transition-all duration-200 p-1.5 rounded-md text-sm opacity-0 group-hover:opacity-100 hover:bg-slate-700/50 text-slate-400 hover:text-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Regenerate title with AI"
                                >
                                  {isRegeneratingTitle ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Sparkles className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setIsEditingTitle(true)}
                                  className="transition-opacity duration-200 px-2 py-1 rounded-md text-sm opacity-0 group-hover:opacity-100 hover:bg-slate-700/50 text-slate-400 hover:text-blue-400"
                                  title="Edit title"
                                >
                                  Rename
                                </button>
                              </>
                            )}
                          </div>
                          {/* <ProcessingTimer 
                                                    processingTimeSeconds={selectedSOP?.processingTimeSeconds}
                                                    className="ml-0"
                                                    tooltip="Total SOP processing time"
                                                    status={selectedSOP?.processingStatus as ProcessingStatusEnum}
                                                    errorMessage={
                                                      selectedSOP?.processingStatus ===
                                                      'error'
                                                        ? 'Processing failed'
                                                        : null
                                                    }
                                                /> */}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="sop-actions">
                    {((hasUnsavedChanges && isEditable && canEdit) ||
                      (reviewMode &&
                        candidates.length > 0 &&
                        isSuperAdmin &&
                        candidatesDirty &&
                        canEdit)) && (
                      <button
                        className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer bg-gradient-to-r from-green-500/20 to-green-500/10 border border-green-500/30 text-green-400 hover:from-green-500/30 hover:to-green-500/20 hover:border-green-500/40 hover:text-green-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        onClick={async () => {
                          if (
                            reviewMode &&
                            candidates.length > 0 &&
                            isSuperAdmin &&
                            sopId &&
                            latestVersion?.versionId
                          ) {
                            if (candidatesDirty) {
                              setIsSavingCandidates(true);
                              const result = await saveCandidates({
                                sopId,
                                versionId: latestVersion.versionId,
                              });
                              setIsSavingCandidates(false);
                              if (result.success) {
                                showToast(
                                  "Saved",
                                  "AI version changes saved.",
                                  "success",
                                );
                              } else {
                                showToast(
                                  "Error",
                                  result.error ?? "Failed to save.",
                                  "error",
                                );
                                return;
                              }
                            }

                            if (hasUnsavedChanges) {
                              await handleSaveAllChanges();
                            }
                            return;
                          }

                          await handleSaveAllChanges();
                        }}
                        disabled={
                          reviewMode &&
                          candidates.length > 0 &&
                          isSuperAdmin &&
                          candidatesDirty
                            ? isSavingCandidates
                            : isUpdating
                        }
                      >
                        {reviewMode &&
                        candidates.length > 0 &&
                        isSuperAdmin &&
                        isSavingCandidates ? (
                          <>
                            <Loader className="animate-spin w-4 h-4" />
                            <span>Saving...</span>
                          </>
                        ) : isUpdating ? (
                          <>
                            <Loader className="animate-spin w-4 h-4" />
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                              />
                            </svg>
                            <span>Save Changes</span>
                          </>
                        )}
                      </button>
                    )}

                    {isSuperAdmin &&
                      selectedSOP?.processingStatus ===
                        ProcessingStatusEnum.PENDING_VERIFICATION &&
                      (isVerifying ? (
                        <div className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 opacity-75 cursor-not-allowed bg-slate-600/10 border border-slate-600/20 text-slate-400">
                          <Loader className="animate-spin w-4 h-4" />
                          <span>Verifying...</span>
                        </div>
                      ) : (
                        <button
                          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer bg-gradient-to-r from-blue-500/20 to-blue-500/10 border border-blue-500/30 text-blue-400 hover:from-blue-500/30 hover:to-blue-500/20 hover:border-blue-500/40 hover:text-blue-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-transparent disabled:hover:border-blue-500/30"
                          onClick={handleFinishVerification}
                          disabled={hasUnsavedChanges}
                          title={
                            hasUnsavedChanges
                              ? "Save your changes first"
                              : undefined
                          }
                        >
                          <ClipboardCheck className="w-4 h-4" />
                          <span>Finish Verification</span>
                        </button>
                      ))}

                    {isEditable &&
                      canEdit &&
                      !reviewMode &&
                      selectedSOP?.processingStatus !==
                        ProcessingStatusEnum.PENDING_VERIFICATION && (
                        <button
                          type="button"
                          disabled={
                            hasUnsavedChanges ||
                            isRegenerationRequested ||
                            candidates.length > 0
                          }
                          title={
                            isRegenerationRequested
                              ? "AI is preparing proposed changes — review them before releasing"
                              : candidates.length > 0
                                ? "Review all AI proposals before releasing"
                                : hasUnsavedChanges
                                  ? "Save your changes first"
                                  : undefined
                          }
                          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer bg-gradient-to-r from-slate-500/20 to-slate-500/10 border border-slate-500/30 text-slate-200 hover:from-slate-500/30 hover:to-slate-500/20 hover:border-slate-500/40 hover:text-slate-100 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-transparent disabled:hover:border-slate-500/30"
                          onClick={() => setCompleteVersionOpen(true)}
                        >
                          <CircleCheck className="w-4 h-4" />
                          <span>Release</span>
                        </button>
                      )}
                    {canDownload &&
                      (!downloading && !exportingMarkdown ? (
                        <button
                          type="button"
                          className="flex items-center gap-2 px-3 py-1 rounded font-medium text-sm transition-all duration-200 cursor-pointer bg-gradient-to-r from-blue-500/20 to-blue-500/10 border border-blue-500/30 text-blue-400 hover:from-blue-500/30 hover:to-blue-500/20 hover:border-blue-500/40 hover:text-blue-300 hover:shadow-lg hover:shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-500/20 disabled:hover:to-blue-500/10 disabled:hover:border-blue-500/30 disabled:hover:text-blue-400"
                          disabled={
                            !versionInContext?.steps?.length ||
                            hasUnsavedChanges
                          }
                          title={
                            hasUnsavedChanges
                              ? "Save your changes first"
                              : !versionInContext?.steps?.length
                                ? "Save the SOP at least once before downloading"
                                : undefined
                          }
                          onClick={() => {
                            const isPendingVerification =
                              selectedSOP?.processingStatus ===
                              ProcessingStatusEnum.PENDING_VERIFICATION;
                            if (
                              isEditable &&
                              canEdit &&
                              !isPendingVerification &&
                              !reviewMode
                            ) {
                              setDownloadReleaseChoiceOpen(true);
                            } else {
                              setDownloadFormatPickerOpen(true);
                            }
                          }}
                        >
                          <DownloadCloud className="w-4 h-4" />
                          <span>Download</span>
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 opacity-75 cursor-not-allowed bg-slate-600/10 border border-slate-600/20 text-slate-400">
                          <Loader className="animate-spin w-4 h-4" />
                          <span>Downloading...</span>
                        </div>
                      ))}
                  </div>
                </div>
                <StartReviewModal
                  open={startReviewOpen}
                  onClose={() => setStartReviewOpen(false)}
                  sopId={sopId || ""}
                  versionId={latestVersion?.versionId}
                  steps={latestVersion?.steps ?? []}
                  referenceFiles={referenceFiles}
                  onReferenceFilesChange={setReferenceFiles}
                  referenceFileKinds={referenceFileKinds}
                  defaultReferenceFileKind={defaultReferenceFileKind}
                  onDefaultReferenceFileKindChange={setDefaultReferenceFileKind}
                  onReferenceFileKindChange={setReferenceFileKind}
                  selectedStepIndices={selectedStepIndices}
                  onSelectedStepIndicesChange={setSelectedStepIndices}
                  selectedFeedbackIds={selectedFeedbackIds}
                  onSelectedFeedbackIdsChange={setSelectedFeedbackIds}
                  primaryLabel="Regenerate with AI"
                  isRegenerating={isRegenerating}
                  regenerateError={regenerateError}
                  onPrimary={async () => {
                    if (!sopId || !latestVersion?.versionId) return;

                    if (hasUnsavedChanges) {
                      const saved = await handleSaveAllChanges();
                      if (!saved) return;
                    }

                    clearCandidates();
                    clearDecisions();
                    track("sop_file_uploaded", {
                      type: "regenerate_sop",
                      sop_id: sopId,
                      step_count: selectedStepIndices.length,
                    });
                    const result = await regenerate({
                      sopId,
                      versionId: latestVersion.versionId,
                    });
                    setStartReviewOpen(false);
                    if (result.success) {
                      track("sop_proposed_version_generated", {
                        sop_id: sopId,
                        version_id: latestVersion.versionId,
                        step_count: selectedStepIndices.length,
                      });
                      await fetchSOPById(sopId, latestVersion.versionId);
                      refreshSelectedSOP();
                      setReviewMode(true);
                    } else {
                      showToast(
                        "Could not start update",
                        result.error ??
                          regenerateError ??
                          "AI is preparing the update for selected steps and feedback.",
                        "error",
                      );
                    }
                  }}
                />
                <Modal
                  open={exitReviewConfirmOpen}
                  onClose={() => setExitReviewConfirmOpen(false)}
                  title="Unsaved changes"
                  size="small"
                >
                  <p className="text-sm text-slate-200 mb-4">
                    You have unsaved changes to AI versions. Leave anyway? Your
                    edits will be lost.
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      className="px-3 py-2 rounded-md border border-slate-600/60 bg-slate-900/40 text-xs font-semibold text-slate-200 hover:bg-slate-800/60"
                      onClick={() => setExitReviewConfirmOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      className="px-3 py-2 rounded-md border border-red-500/60 bg-red-500/20 text-xs font-semibold text-red-200 hover:bg-red-500/30"
                      onClick={() => {
                        setExitReviewConfirmOpen(false);
                        setReviewMode(false);
                      }}
                    >
                      Leave anyway
                    </Button>
                  </div>
                </Modal>
                <Modal
                  open={leaveStepChangesConfirmOpen}
                  onClose={() => {
                    setLeaveStepChangesConfirmOpen(false);
                    setPendingNavigation(null);
                  }}
                  title="Unsaved changes"
                  size="small"
                >
                  <p className="text-sm text-slate-200 mb-4">
                    You have unsaved step changes. Save before leaving?
                    Otherwise they will be lost.
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      className="px-3 py-2 rounded-md border border-slate-600/60 bg-slate-900/40 text-xs font-semibold text-slate-200 hover:bg-slate-800/60"
                      onClick={() => {
                        setLeaveStepChangesConfirmOpen(false);
                        setPendingNavigation(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      className="px-3 py-2 rounded-md border border-red-500/60 bg-red-500/20 text-xs font-semibold text-red-200 hover:bg-red-500/30"
                      onClick={() => discardStepChangesAndNavigate()}
                    >
                      Don&apos;t save
                    </Button>
                    <Button
                      type="button"
                      className="px-3 py-2 rounded-md border border-green-500/60 bg-green-500/20 text-xs font-semibold text-green-200 hover:bg-green-500/30"
                      onClick={async () => {
                        const saved = await handleSaveAllChanges();
                        if (saved && pendingNavigation) {
                          setHasUnsavedChanges(false);
                          navigate(
                            pendingNavigation.path,
                            pendingNavigation.state != null
                              ? { state: pendingNavigation.state }
                              : undefined,
                          );
                          setPendingNavigation(null);
                          setLeaveStepChangesConfirmOpen(false);
                        }
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </Modal>
                <div className="step-content-container">
                  {/* {reviewMode && candidates.length > 0 && (
                  <div className="step-content-review-banner">
                    Comparing current steps with AI versions.{" "}
                    {isSuperAdmin
                      ? "Edit candidates, then save or submit for review."
                      : "Accept or request changes for each candidate, then finalize."}
                  </div>
                )} */}
                  {/* Step Navigation Bar */}
                  {/* <div className="step-navigation">
                  <div className="step-nav-left">
                    <button
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer bg-slate-600/10 text-slate-300 hover:bg-slate-600/20 hover:border-slate-600/30 hover:text-slate-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-600/10 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-slate-600/10 disabled:hover:border-slate-600/20 disabled:hover:text-slate-300"
                      onClick={() => handleStepNavigation(-1)}
                      disabled={index === 0}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                      <span>Previous</span>
                    </button>
                  </div>

                  <div className="step-indicator">
                    <div className="step-counter" data-testid="step-counter">
                      <span className="current-step">{index + 1}</span>
                      <span className="step-separator">/</span>
                      <span className="total-steps">{stepsLength}</span>
                    </div>
                    <div className="step-progress">
                      <div
                        className="progress-bar"
                        style={{
                          width: `${((index + 1) / stepsLength) * 100}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="step-nav-right flex items-center gap-2">
                    <button
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-200 cursor-pointer bg-slate-600/10 border border-slate-600/20 text-slate-300 hover:bg-slate-600/20 hover:border-slate-600/30 hover:text-slate-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-600/10 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-slate-600/10 disabled:hover:border-slate-600/20 disabled:hover:text-slate-300"
                      onClick={() => handleStepNavigation(1)}
                      disabled={index + 1 === stepsLength}
                    >
                      <span>Next</span>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                </div> */}

                  {isLoadingImages ? (
                    <div className="loading-state">
                      <div className="loading-spinner">
                        <Loader className="animate-spin w-8 h-8" />
                      </div>
                      <span className="loading-text">Loading images...</span>
                    </div>
                  ) : reviewMode ? (
                    latestVersion?.steps.map((stepData, idx) => (
                      <div
                        key={`step-review-${idx}`}
                        style={{ display: idx === index ? "block" : "none" }}
                      >
                        {idx === index &&
                          (() => {
                            const candidateRecord =
                              candidates.find((c) => c.stepIndex === idx) ??
                              null;
                            const candidateForStep =
                              candidateRecord?.candidate ?? null;

                            if (candidateForStep) {
                              return (
                                <StepCandidateReview
                                  originalStep={stepData}
                                  candidate={candidateForStep}
                                  stepIndex={idx}
                                  showApproveDenyActions={
                                    showApproveDenyActions
                                  }
                                  sopTitle={selectedSOP?.sopTitle ?? ""}
                                  sopId={sopId || ""}
                                  versionId={latestVersion?.versionId}
                                  candidateId={candidateRecord?.id}
                                  canEdit={canEdit}
                                  canEditCandidate={
                                    isSuperAdmin && !ownerVisibleAny
                                  }
                                  waitingForOwnerReview={
                                    isSuperAdminWaitingForOwnerReview
                                  }
                                  onCandidateChange={(updated) =>
                                    updateCandidate(idx, updated)
                                  }
                                  onAccept={() => {
                                    if (decisions[idx] === "accept") {
                                      undecide(idx);
                                    } else {
                                      decide(idx, "accept");
                                    }
                                  }}
                                  onRequestChange={() => {
                                    if (decisions[idx] === "request_change") {
                                      undecide(idx);
                                    } else {
                                      decide(idx, "request_change");
                                    }
                                  }}
                                  onBeforeEnterRequestChangePending={() => {
                                    if (decisions[idx] === "accept") {
                                      undecide(idx);
                                    }
                                  }}
                                  decision={decisions[idx]}
                                  onFileUpload={handleFileUpload}
                                  getCurrentUrl={getCurrentUrl}
                                  getImageUrls={getImageUrls}
                                  setImageUrls={setImageUrls}
                                  imageUrls={imageUrls}
                                  media={media}
                                  mediaLoading={mediaLoading}
                                  mediaError={mediaError}
                                  stepsLength={stepsLength}
                                />
                              );
                            }
                            // Unlocked step: keep the normal editor usable during review mode.
                            return (
                              <StepEditor
                                index={idx}
                                step={stepData}
                                sopId={sopId || ""}
                                sopTitle={selectedSOP?.sopTitle ?? ""}
                                versionId={versionInContext?.versionId}
                                regenerationLocked={lockedStepSet.has(idx)}
                                regenerationLockedMessage={
                                  "AI is preparing the update. Editing is locked for selected steps."
                                }
                                readOnly={
                                  !isEditable ||
                                  !canEdit ||
                                  lockedStepSet.has(idx) ||
                                  isSuperAdminRegeneratingReview
                                }
                                onFileUpload={handleFileUpload}
                                onStepChange={(updatedStep) =>
                                  handleStepChange(idx, updatedStep)
                                }
                                onDeleteStep={() => handleDeleteStep(idx)}
                                canDelete={
                                  canEdit &&
                                  !lockedStepSet.has(idx) &&
                                  !hasLockedSteps
                                }
                                canEdit={canEdit}
                                canEditContent={canEditStepContent}
                                stepsLength={stepsLength}
                                getCurrentUrl={getCurrentUrl}
                                getImageUrls={getImageUrls}
                                setImageUrls={setImageUrls}
                                imageUrls={imageUrls}
                                media={media}
                                mediaLoading={mediaLoading}
                                mediaError={mediaError}
                              />
                            );
                          })()}
                      </div>
                    ))
                  ) : (
                    versionInContext?.steps.map((stepData, idx) => (
                      <div
                        key={`step-${idx}`}
                        style={{ display: idx === index ? "block" : "none" }}
                      >
                        {idx === index && (
                          <>
                            {/* Locked step content remains editable=false, but we don't show per-step "Awaiting update" badges here. */}
                            <StepEditor
                              index={idx}
                              step={stepData}
                              sopId={sopId || ""}
                              sopTitle={selectedSOP?.sopTitle ?? ""}
                              versionId={versionInContext?.versionId}
                              regenerationLocked={lockedStepSet.has(idx)}
                              regenerationLockedMessage={
                                "AI is preparing the update. Editing is locked for selected steps."
                              }
                              readOnly={
                                !isEditable ||
                                !canEdit ||
                                lockedStepSet.has(idx)
                              }
                              onFileUpload={handleFileUpload}
                              onStepChange={(updatedStep) =>
                                handleStepChange(idx, updatedStep)
                              }
                              onDeleteStep={() => handleDeleteStep(idx)}
                              canDelete={
                                canEdit &&
                                !lockedStepSet.has(idx) &&
                                !hasLockedSteps
                              }
                              canEdit={canEdit}
                              canEditContent={canEditStepContent}
                              stepsLength={stepsLength}
                              getCurrentUrl={getCurrentUrl}
                              getImageUrls={getImageUrls}
                              setImageUrls={setImageUrls}
                              imageUrls={imageUrls}
                              media={media}
                              mediaLoading={mediaLoading}
                              mediaError={mediaError}
                            />
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Right aside – document preview (collapsible) */}
              <ReferencePanel
                sopId={sopId ?? undefined}
                stepSegments={stepSegments}
                referenceFiles={referenceFiles}
                versionId={versionInContext?.versionId ?? undefined}
                defaultStepKey={
                  sopId && versionInContext?.versionId != null
                    ? `${sopId}:${versionInContext.versionId}:${index}`
                    : null
                }
                onSelectFeedback={(feedback) => {
                  if (feedback.stepIndex === TITLE_PAGE_FEEDBACK_STEP_INDEX) {
                    const vid = versionInContext?.versionId;
                    if (!sopId || !vid) return;
                    navigate(
                      `/sop/${sopId}?versionId=${encodeURIComponent(vid)}&feedbackId=${encodeURIComponent(feedback.id)}`,
                      { state: { fromStepView: true } },
                    );
                    return;
                  }
                  setIndex(feedback.stepIndex);
                }}
              />
            </div>
          </div>
        ) : (
          <div className="not-found-state">
            <div className="not-found-content">
              <div className="not-found-icon">
                <svg
                  className="w-16 h-16"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h2 className="not-found-title">SOP Not Found</h2>
              <p className="not-found-description">
                The SOP you're looking for doesn't exist or has been moved.
              </p>
              <button
                className="back-to-sops-btn"
                onClick={() => requestNavigation("/sop-dashboard")}
              >
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Back to SOPs</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {downloadReleaseChoiceOpen && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60"
          onClick={() => setDownloadReleaseChoiceOpen(false)}
        >
          <div
            className="bg-slate-900 border border-slate-600 rounded-xl p-6 w-full max-w-md shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-white mb-4">Download</h3>
            <p className="text-slate-400 text-sm mb-6">
              Have you finished this version and want to release it?
            </p>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                className="w-full px-4 py-2.5 rounded-lg bg-cyan-600 text-white hover:bg-cyan-500 font-medium text-sm"
                onClick={() => {
                  setDownloadReleaseChoiceOpen(false);
                  setReleaseThenDownloadMode(true);
                  setCompleteVersionOpen(true);
                }}
              >
                Yes, release and download
              </button>
              <button
                type="button"
                className="w-full px-4 py-2.5 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 font-medium text-sm"
                onClick={() => {
                  setDownloadReleaseChoiceOpen(false);
                  setDownloadFormatPickerOpen(true);
                }}
              >
                No, just download
              </button>
            </div>
          </div>
        </div>
      )}

      {downloadFormatPickerOpen && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60"
          onClick={() => {
            setDownloadFormatPickerOpen(false);
            pendingPostReleaseNavigateRef.current = null;
          }}
        >
          <div
            className="bg-slate-900 border border-slate-600 rounded-xl p-6 w-full max-w-md shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-white mb-4">
              Choose format
            </h3>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                className="w-full px-4 py-2.5 rounded-lg bg-slate-700 text-slate-200 hover:bg-slate-600 font-medium text-sm"
                onClick={async () => {
                  setDownloadFormatPickerOpen(false);
                  await handleDownloadWithValidation(downloadPdf);
                  const pending = pendingPostReleaseNavigateRef.current;
                  if (pending) {
                    pendingPostReleaseNavigateRef.current = null;
                    await fetchSOPs();
                    await fetchSOPById(
                      pending.sopId,
                      pending.completed.versionId,
                    );
                    setSelectedVersion(pending.completed);
                    navigate(
                      `/sop/${pending.sopId}?versionId=${encodeURIComponent(pending.completed.versionId)}`,
                    );
                    showToast(
                      "Version released",
                      "You are viewing the released version on the title page.",
                      "success",
                    );
                  }
                }}
              >
                Download PDF
              </button>
              <button
                type="button"
                className="w-full px-4 py-2.5 rounded-lg bg-slate-700 text-slate-200 hover:bg-slate-600 font-medium text-sm"
                onClick={async () => {
                  setDownloadFormatPickerOpen(false);
                  await handleDownloadWithValidation(downloadMarkdown);
                  const pending = pendingPostReleaseNavigateRef.current;
                  if (pending) {
                    pendingPostReleaseNavigateRef.current = null;
                    await fetchSOPs();
                    await fetchSOPById(
                      pending.sopId,
                      pending.completed.versionId,
                    );
                    setSelectedVersion(pending.completed);
                    navigate(
                      `/sop/${pending.sopId}?versionId=${encodeURIComponent(pending.completed.versionId)}`,
                    );
                    showToast(
                      "Version released",
                      "You are viewing the released version on the title page.",
                      "success",
                    );
                  }
                }}
              >
                Download Markdown
              </button>
            </div>
          </div>
        </div>
      )}

      {completeVersionOpen && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60"
          onClick={() => {
            if (!isCompletingVersion) {
              setCompleteVersionOpen(false);
              setReleaseThenDownloadMode(false);
            }
          }}
        >
          <div
            className="bg-slate-900 border border-slate-600 rounded-xl p-6 w-full max-w-md shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-white mb-4">
              {releaseThenDownloadMode
                ? "Release and download"
                : "Complete version"}
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              The current version will be marked as done. A new draft version
              will be created automatically.
              {releaseThenDownloadMode &&
                " Then choose PDF or Markdown to download."}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Description (required)
                </label>
                <input
                  type="text"
                  value={completeVersionDescription}
                  onChange={(e) =>
                    setCompleteVersionDescription(e.target.value)
                  }
                  placeholder="e.g. Initial release"
                  className="w-full px-3 py-2 rounded-lg border border-slate-600 bg-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                  disabled={isCompletingVersion}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                onClick={() => {
                  if (!isCompletingVersion) {
                    setCompleteVersionOpen(false);
                    setReleaseThenDownloadMode(false);
                  }
                }}
                disabled={isCompletingVersion}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-50 flex items-center gap-2"
                onClick={handleCompleteVersion}
                disabled={
                  isCompletingVersion || !completeVersionDescription.trim()
                }
              >
                {isCompletingVersion ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Completing...
                  </>
                ) : releaseThenDownloadMode ? (
                  "Complete and choose format"
                ) : (
                  "Complete version"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast
        open={toast.open}
        onOpenChange={hideToast}
        title={toast.title || ""}
        description={toast.description}
        type={toast.type}
        duration={toast.duration}
      />
    </>
  );
};

export default SopEditor;
